import { type RouteLocationRaw } from "vue-router";
import urlSlug from "url-slug";
import { createLocalizedRoute } from "@/router/helpers";
import type { Filter as MyItemsFilter } from "@/services/collection";
import { ItemStatusToUrlString } from "@/services/collection";
import { type UrlFlags as ChatUrlFlags } from "@/composables/chat";
import { RouteName } from "@/router/routeNameEnum";
import { type OnlyPublicProperties, type Uuid } from "@/types";
import { type Category } from "@/services/collection/categories";
import type { CollectorsFilter } from "@/services/collectors";
import { type CollectionItem_ListItem, type CollectionItem } from "@/services/collection/item";
import type { PublicCollectionItem_FromCollectible } from "@/services/collection/item/public";
import { type PublicCollectionItem_ListItem, type PublicCollectionItem, PublicCollectionItemsFilter } from "@/services/collection/item/public";
import type { CollectibleId } from "@/services/collectibles";
import { type UserId } from "@/services/user";
import { type MarketplaceItemId } from "@/services/marketplace/item";
import { type OrderId } from "@/services/marketplace/order";
import { type Model as MarketplaceFilter } from "@/composables/marketplace";

export const createMyItemDetailLink = (item: PublicCollectionItem_ListItem | CollectionItem | PublicCollectionItem | CollectionItem_ListItem | PublicCollectionItem_FromCollectible, hash?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MyItemDetail,
    params: {
      id: item.id,
      nameSeoSlug: urlSlug(item.name),
    },
    hash,
  });

export const createDashboardLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Dashboard });

export const createAccountSettingsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.AccountSettings });

export const createYearInReviewLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.YearInReview });

export const createMyProfileLink = (filter?: PublicCollectionItemsFilter.Filter): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collector,
    query: filter ? PublicCollectionItemsFilter.createUrlParams(filter) : undefined,
  });

export const createChatLink = (buddyId?: UserId, buddyNickname?: string, flags?: ChatUrlFlags): RouteLocationRaw => {
  const route: RouteLocationRaw = {
    name: RouteName.Chat,
  };

  if (buddyId !== undefined && buddyNickname !== undefined) {
    route.params = { buddyId, buddyNickname };
  }

  if (flags !== undefined) {
    route.query = flags;
  }

  return createLocalizedRoute(route);
};

export const createPortfolioLink = (filter?: MyItemsFilter.Filter | OnlyPublicProperties<MyItemsFilter.Filter>): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Portfolio,
    params: {
      itemStatus: filter?.itemStatus ? ItemStatusToUrlString[filter.itemStatus] : undefined,
    },
    query: filter?.createUrlQuery(),
  });

export const createAiHistoryLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.AiHistory });

export const createError404Link = (): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Error404,
    params: {
      pathMatch: "404",
    },
  });

export const createRegistrationLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Registration });

export const createLoginLink = (redirectAfterLogin?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Login,
    query: {
      redirect_url: redirectAfterLogin ?? undefined,
    },
  });

export const createPasswordRestoreLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.PasswordRestore });

export const createAccountSetupLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.AccountSetup });

export const createCollectorLink = (nickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collector,
    params: { nickname },
  });

export const createHomepageLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Homepage });

export const createPublicItemDetailLink = (itemId: number, itemName: string, collectorNickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.PublicItemDetail,
    params: {
      nickname: urlSlug(collectorNickname),
      itemId: itemId,
      slug: urlSlug(itemName) || "-",
    },
  });

export const createAboutLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.About });

export const createGdprLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Gdpr });
export const createTacLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Tac });
export const createGuideLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Guide });

export const createArticleLink = (id: number, url: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Article,
    params: { id, url },
  });

export const createMagazineLink = (category?: Category): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Magazine,
    params: {
      category: category ? category.url : undefined,
    },
  });

export const createContactsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Contacts });

export const createShoppingCartLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.ShoppingCart });
export const createMarketplaceOrderLink = (sellerNickname: string): RouteLocationRaw => createLocalizedRoute({ name: RouteName.MarketplaceOrder, params: { sellerNickname } });

export const createCollectorsLink = (filter?: CollectorsFilter.Filter): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collectors,
    query: filter?.createUrlParams(),
  });

export const createAiLookupFormLink = (categoryUrl?: string): RouteLocationRaw => {
  if (categoryUrl !== undefined) {
    return createLocalizedRoute({ name: RouteName.AiFormWithPreselectedCategory, params: { seoSlug: categoryUrl } });
  } else {
    return createLocalizedRoute({ name: RouteName.AiForm });
  }
};
export const createAiLandingPageLink = (categorySeoSlug?: string): RouteLocationRaw => {
  if (categorySeoSlug === undefined) {
    return createLocalizedRoute({ name: RouteName.AiLandingPage });
  } else {
    return createLocalizedRoute({ name: RouteName.AiLandingPageWithCategory, params: { categorySeoSlug } });
  }
};
export const createAiDetailLink = (itemId: Uuid): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.AiDetail,
    params: { itemId },
  });

/**
 * Catalogue link factories ⤵️
 */

export const createCatalogueLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Catalogue });
export const createCollectibleLink = (category: Category, itemId: CollectibleId): RouteLocationRaw => {
  const categoryUrl = category.url;
  return createLocalizedRoute({
    name: RouteName.CatalogueItem,
    params: { categoryUrl, itemId },
  });
};

export const createCatalogueCategoryLink = (category: Category): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueCategory,
    params: {
      categoryUrl: category.url,
    },
  });

export const createSavedItemsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.SavedItems });

export const createSalesLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Sales });
export const createSaleLink = (orderId: OrderId): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Sale, params: { orderId } });
export const createPurchasesLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Purchases });
export const createPurchaseLink = (orderId: OrderId): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Purchase, params: { orderId } });
export const createMarketplaceLink = (filter?: MarketplaceFilter): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Marketplace, query: filter });
export const createMarketplaceItemLink = (itemId: MarketplaceItemId, itemName: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItem,
    params: { itemId, seoSlug: urlSlug(itemName) },
  });

export const createSupportLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Support });
export const createMarketplaceItemCreatedLink = (itemId: MarketplaceItemId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItemCreated,
    params: { itemId },
  });
